import React, {Fragment} from 'react';

import {useSnackbar} from 'notistack';
import {Box, Divider, Typography} from '@material-ui/core';

import {useSpinner} from 'hooks';
import {ClientService, SecurityService} from 'services';
import {MainContent} from 'layouts/commons';
import {EfeResponse, SecurityUsersResponse} from 'commons/models';
import {
    Messages,
    Roles,
    SnackbarVariants,
    TABLE_PER_PAGE,
} from 'commons/constants';
import DashboardUsers from './dashboard-users/DashboardUsers';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import LogErrors from './log-errors/LogErrors';
import {LogErrorsResponse} from '../../commons/models/dtos/logs/Logs';

function Dashboard() {
    const {expedientId, fatherKycId} = useMenuContext();
    const {enqueueSnackbar} = useSnackbar();

    const [page, setPage] = React.useState<number>(1);
    const [status, setStatus] = React.useState<string>();
    const [personKey, setPersonKey] = React.useState<string>();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [users, setUsers] = React.useState<SecurityUsersResponse>();
    const [logs, setLogs] = React.useState<LogErrorsResponse>();
    const [rowsPerPage, setRowsPerPage] = React.useState<number>(TABLE_PER_PAGE);

    async function getData(personKey: string, page: number, perPage: number, status?: string) {
        try {
            setIsLoading(true);
            const response: EfeResponse<SecurityUsersResponse> = await SecurityService.getUsers({
                page: page,
                size: rowsPerPage,
                expedientId,
                status: status,
                personKey: personKey || undefined,
            });
            if (response && response.data && response.data.data && response.data.data.length) {
                setUsers(response.data);
            }
            setIsLoading(false);
        } catch (error: any) {
            setIsLoading(false);
            setUsers(undefined);
            if (error.code === 404) {
                enqueueSnackbar(Messages.USERS_NOT_FOUND, {
                    variant: SnackbarVariants.WARNING,
                });
            } else {
                enqueueSnackbar(Messages.SECURITY_USERS_ERROR, {
                    variant: SnackbarVariants.WARNING,
                });
            }
        }
    }

    async function getLogsData(personKey: number | string) {
        try {
            setIsLoading(true);
            const response: EfeResponse<LogErrorsResponse> = await ClientService.getLogs({
                page: 1,
                pageSize: 100,
                fatherKycId: personKey,
            });
            if (response && response.data && response.data.data && response.data.data.length) {
                setLogs(response.data);
            }
            setIsLoading(false);
        } catch (e: any) {
            setIsLoading(false);
            setLogs(undefined);
            if (e.code === 404) {
                enqueueSnackbar(Messages.LOGS_NOT_FOUND, {
                    variant: SnackbarVariants.WARNING,
                });
            } else {
                enqueueSnackbar(Messages.LOGS_ERROR, {
                    variant: SnackbarVariants.WARNING,
                });
            }
        }
    }

    async function getUsers(userRole?: string, status?: string, personKey?: string) {
        setPage(1);
        setRowsPerPage(TABLE_PER_PAGE);
        if (userRole) {
            setStatus(status);
            if (userRole === Roles.CLIENT) {
                setPersonKey('');
                await getLogsData(fatherKycId);
                getData('', 1, TABLE_PER_PAGE, status);
            } else {
                setPersonKey(personKey || '');
                await getLogsData(personKey || '');
                getData(personKey || '', 1, TABLE_PER_PAGE, status);
            }
        } else {
            setUsers(undefined);
        }
    }

    async function getLogs() {
        await getLogsData(fatherKycId);
    }

    function handleChangePage(page: number) {
        setPage(page);
        getData(personKey || '', page, rowsPerPage, status);
    }

    function handleChangeRowsPerPage(perPage: number) {
        setPage(1);
        setRowsPerPage(perPage);
        getData(personKey || '', 1, perPage, status);
    }

    React.useEffect(() => {
        getUsers(undefined);
        getLogs();
    }, []);

    const NoUserView = (
        <Fragment>
            <Box p={1}>
                <Typography
                    variant={'h3'}>{'No cuentas con usuarios registrados'}</Typography>
            </Box>
            <Box p={1} pb={8}>
                <Typography variant={'body2'}>
                    {'Agrega tu primer usuario haciendo click en el boton abajo.'}
                </Typography>
            </Box>
        </Fragment>
    );

    return (
        <Fragment>
            {isLoading && useSpinner(isLoading)}
            <MainContent>
                <DashboardUsers
                    users={users}
                    getUsers={getUsers}
                    view={!users || !users.data ? NoUserView : <></>}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
                <Divider />
                <LogErrors
                    page={1}
                    rowsPerPage={1000}
                    handleChangePage={() => {
                        console.log('');
                    }}
                    handleChangeRowsPerPage={() => {
                        console.log('');
                    }}
                    getErrors={getLogsData}
                    data={logs}
                    kycId={personKey || fatherKycId}
                />
            </MainContent>
        </Fragment>
    );
}

export default Dashboard;
